import { CSSProperties } from 'react';

export interface TransitionMetrics {
  progress: number;
  start: number;
  end: number;
}

interface SharedPhotoStyleParams {
  sourceTop: number;
  sourceLeft: number;
  sourceWidth: number;
  sourceHeight: number;
  targetTop: number;
  targetLeft: number;
  targetWidth: number;
  targetHeight: number;
  progress: number;
  isVisible: boolean;
}

interface StickyHeaderLayoutParams {
  wrapperRect: DOMRect;
  sourceRect: DOMRect;
  sourceWidth: number;
  sourceHeight: number;
  targetRect: DOMRect;
  currentScrollTop: number;
  scrollContainerTop: number;
  transitionStartOffset?: number;
  snapCompletionOffset?: number;
}

export interface StickyHeaderLayoutState {
  progress: number;
  isStickyRecipeHeaderVisible: boolean;
  isSharedPhotoVisible: boolean;
  sharedPhotoStyle: CSSProperties;
  transitionMetrics: TransitionMetrics;
}

const clampProgress = (value: number) => Math.min(1, Math.max(0, value));

export const isZeroSizedRect = (rect: DOMRect) => rect.width === 0 && rect.height === 0;

export const buildSharedPhotoStyle = ({
  sourceTop,
  sourceLeft,
  sourceWidth,
  sourceHeight,
  targetTop,
  targetLeft,
  targetWidth,
  targetHeight,
  progress,
  isVisible,
}: SharedPhotoStyleParams): CSSProperties => {
  const top = sourceTop + (targetTop - sourceTop) * progress;
  const left = sourceLeft + (targetLeft - sourceLeft) * progress;
  const width = sourceWidth + (targetWidth - sourceWidth) * progress;
  const height = sourceHeight + (targetHeight - sourceHeight) * progress;
  const borderRadius = 4 + (12 - 4) * progress;

  return {
    top,
    left,
    width,
    height,
    borderRadius: `${borderRadius}px`,
    opacity: isVisible ? 1 : 0,
    pointerEvents: isVisible ? 'auto' : 'none',
  };
};

export const calculateStickyHeaderLayoutState = ({
  wrapperRect,
  sourceRect,
  sourceWidth,
  sourceHeight,
  targetRect,
  currentScrollTop,
  scrollContainerTop,
  transitionStartOffset = 88,
  snapCompletionOffset = 72,
}: StickyHeaderLayoutParams): StickyHeaderLayoutState => {
  const stickyTargetHeight = 96;
  const transitionDistance = Math.max(wrapperRect.height - stickyTargetHeight, 1);
  const progress = clampProgress((-wrapperRect.top) / transitionDistance);
  const sourceTop = sourceRect.top + (sourceRect.height - sourceHeight) / 2;
  const sourceLeft = sourceRect.left + (sourceRect.width - sourceWidth) / 2;
  const currentFramePadding = 15 * (1 - progress);
  const compensatedTargetTop = targetRect.top - currentFramePadding;
  const compensatedTargetLeft = targetRect.left - currentFramePadding;
  const compensatedTargetWidth = targetRect.width + currentFramePadding * 2;
  const compensatedTargetHeight = targetRect.height + currentFramePadding * 2;
  const wrapperTopInScrollSpace = currentScrollTop + wrapperRect.top - scrollContainerTop;
  const isSharedPhotoVisible = progress > 0 && progress < 1;

  return {
    progress,
    isStickyRecipeHeaderVisible: progress >= 1,
    isSharedPhotoVisible,
    sharedPhotoStyle: buildSharedPhotoStyle({
      sourceTop,
      sourceLeft,
      sourceWidth,
      sourceHeight,
      targetTop: compensatedTargetTop,
      targetLeft: compensatedTargetLeft,
      targetWidth: compensatedTargetWidth,
      targetHeight: compensatedTargetHeight,
      progress,
      isVisible: isSharedPhotoVisible,
    }),
    transitionMetrics: {
      progress,
      start: Math.max(wrapperTopInScrollSpace - transitionStartOffset, 0),
      end: wrapperTopInScrollSpace + transitionDistance + snapCompletionOffset,
    },
  };
};

export const resolveAutoSnapTargetScrollTop = (
  metrics: TransitionMetrics,
  lastScrollDirection: 1 | -1 | 0
) => {
  if (lastScrollDirection === -1) {
    return metrics.start;
  }

  if (lastScrollDirection === 1) {
    return metrics.end;
  }

  return metrics.progress >= 0.5 ? metrics.end : metrics.start;
};

export const shouldCompleteStickyTransition = (
  metrics: TransitionMetrics | null,
  delta: number
) => !!metrics && metrics.progress > 0 && metrics.progress < 1 && delta !== 0;
